import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
} from '@mui/material';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import {
  CollapsibleTableProps,
  CalculatedRowData,
  RegionData,
  TimePeriodData,
} from './config';
import './collapsibleTable.css';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Loading from '../../Loading/Loading';

const CollapsibleTable: React.FC<CollapsibleTableProps> = ({
  data,
  isLoading = false,
  className = '',
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Check if data is empty or null
  const hasData =
    data && typeof data === 'object' && Object.keys(data).length > 0;

  // Determine data type and extract time period columns
  const { timeColumns, dataType } = useMemo(() => {
    if (!hasData) return { timeColumns: [], dataType: 'monthly' };

    const timePeriods = new Set<string>();
    let detectedType: 'monthly' | 'daily' = 'monthly';

    const extractTimePeriods = (obj: any) => {
      if (obj && typeof obj === 'object') {
        if (obj.monthly) {
          detectedType = 'monthly';
          Object.keys(obj.monthly).forEach((period) => timePeriods.add(period));
        }

        if (obj.daily) {
          detectedType = 'daily';
          Object.keys(obj.daily).forEach((period) => timePeriods.add(period));
        }
        Object.values(obj).forEach((value) => {
          if (typeof value === 'object' && value !== null) {
            extractTimePeriods(value);
          }
        });
      }
    };

    extractTimePeriods(data);

    // Sort time periods
    const sortedPeriods = Array.from(timePeriods).sort((a, b) => {
      if (detectedType === 'monthly') {
        return new Date(a).getTime() - new Date(b).getTime();
      } else {
        // For daily data, sort numerically
        return parseInt(a) - parseInt(b);
      }
    });

    return { timeColumns: sortedPeriods, dataType: detectedType };
  }, [data, hasData]);

  // Calculate cost value from string
  const calculateCostValue = (costStr: string): number => {
    if (!costStr) return 0;
    const cleanCost = costStr.replace(/[$,]/g, '');
    return parseFloat(cleanCost) || 0;
  };

  // Calculate total cost for time period data (monthly or daily)
  const calculateTotalCost = (
    timePeriodData: TimePeriodData | undefined
  ): number => {
    if (!timePeriodData || typeof timePeriodData !== 'object') return 0;

    return Object.values(timePeriodData).reduce((total, costData) => {
      if (!costData || !costData.cost) return total;
      return total + calculateCostValue(costData.cost);
    }, 0);
  };

  // Calculate time period totals efficiently - ONLY from service level data
  const timePeriodTotals = useMemo(() => {
    if (!hasData) return {};

    const totals: { [period: string]: number } = {};
    const timeKey = dataType === 'monthly' ? 'monthly' : 'daily';

    // Initialize totals for each time period
    timeColumns.forEach((period) => {
      totals[period] = 0;
    });

    // Only calculate from top-level service data (data[serviceName][timeKey])
    Object.entries(data).forEach(([serviceName, serviceData]: any) => {
      if (
        serviceData &&
        typeof serviceData === 'object' &&
        serviceData[timeKey]
      ) {
        Object.entries(serviceData[timeKey]).forEach(
          ([period, costData]: [string, any]) => {
            if (totals[period] !== undefined && costData && costData.cost) {
              totals[period] += calculateCostValue(costData.cost);
            }
          }
        );
      }
    });

    return totals;
  }, [data, timeColumns, hasData, dataType]);

  // Calculate grand total
  const grandTotal = useMemo(() => {
    if (!hasData) return 0;
    return Object.values(timePeriodTotals).reduce(
      (sum, value) => sum + value,
      0
    );
  }, [timePeriodTotals, hasData]);

  // Process data into hierarchical structure
  const processedData = useMemo(() => {
    if (!hasData) return [];

    const result: CalculatedRowData[] = [];
    const timeKey = dataType === 'monthly' ? 'monthly' : 'daily';

    Object.entries(data).forEach(([serviceName, serviceData]: any) => {
      if (!serviceData || typeof serviceData !== 'object') return;

      const serviceRow: CalculatedRowData = {
        name: serviceName,
        totalCost: serviceData[timeKey]
          ? calculateTotalCost(serviceData[timeKey])
          : 0,
        monthlyData: serviceData[timeKey] || {},
        children: [],
        level: 0,
      };

      // Process regions and sub-services
      Object.entries(serviceData).forEach(([key, value]) => {
        if (
          key !== 'monthly' &&
          key !== 'daily' &&
          typeof value === 'object' &&
          value !== null
        ) {
          const regionData = value as RegionData;
          const regionRow: CalculatedRowData = {
            name: key,
            totalCost: regionData[timeKey]
              ? calculateTotalCost(regionData[timeKey])
              : 0,
            monthlyData: regionData[timeKey] || {},
            children: [],
            level: 1,
          };

          // Process sub-services within region
          Object.entries(regionData).forEach(([subKey, subValue]) => {
            if (
              subKey !== 'monthly' &&
              subKey !== 'daily' &&
              typeof subValue === 'object' &&
              subValue !== null
            ) {
              const subServiceData = subValue as any;
              if (subServiceData[timeKey]) {
                regionRow.children?.push({
                  name: subKey,
                  totalCost: calculateTotalCost(subServiceData[timeKey]),
                  monthlyData: subServiceData[timeKey],
                  level: 2,
                  rate: subServiceData.rate,
                });
              }
            }
          });

          serviceRow.children?.push(regionRow);
        }
      });

      result.push(serviceRow);
    });

    return result;
  }, [data, hasData, dataType]);

  // Get all possible row IDs for expand/collapse all functionality
  const getAllRowIds = useMemo(() => {
    const rowIds: string[] = [];

    const collectRowIds = (
      rows: CalculatedRowData[],
      parentId: string = ''
    ) => {
      rows.forEach((row, index) => {
        const rowId = `${parentId}-${index}`;
        if (row.children && row.children.length > 0) {
          rowIds.push(rowId);
          collectRowIds(row.children, rowId);
        }
      });
    };

    collectRowIds(processedData);
    return rowIds;
  }, [processedData]);

  const toggleExpand = (rowId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  const handleExpandAll = () => {
    if (expandedRows.size > 0) {
      setExpandedRows(new Set());
    } else {
      setExpandedRows(new Set(getAllRowIds));
    }
  };

  const areAllRowsExpanded =
    expandedRows.size === getAllRowIds.length && getAllRowIds.length > 0;

  const getTrendClass = (trend: string): string => {
    switch (trend) {
      case 'green':
        return 'ct-trendUp';

      case 'red':
        return 'ct-trendDown';
      case 'orange':
        return 'ct-trendChange';
      case 'black':
        return 'ct-trendEqual';
      default:
        return 'ct-trendEqual';
    }
  };

  const formatCost = (cost: number): string => {
    return `$${cost.toFixed(2)}`;
  };

  const renderRow = (
    row: CalculatedRowData,
    index: number,
    parentId: string = ''
  ): React.ReactNode => {
    const rowId = `${parentId}-${index}`;
    const isExpanded = expandedRows.has(rowId);
    const hasChildren = row.children && row.children.length > 0;

    const getRowClass = (level: number): string => {
      switch (level) {
        case 0:
          return 'ct-serviceRow';
        case 1:
          return 'ct-regionRow';
        case 2:
          return 'ct-subServiceRow';
        default:
          return '';
      }
    };

    const getNameClass = (level: number): string => {
      switch (level) {
        case 0:
          return 'ct-serviceName';
        case 1:
          return 'ct-regionName';
        case 2:
          return 'ct-subServiceName';
        default:
          return '';
      }
    };

    const renderServiceTotal = (
      serviceRow: CalculatedRowData
    ): React.ReactNode => {
      if (serviceRow.level === 0 && hasChildren && isExpanded) {
        // Calculate service-level totals for each time period
        const serviceTotals: { [period: string]: number } = {};
        timeColumns.forEach((period) => {
          serviceTotals[period] = 0;
          if (serviceRow.children) {
            serviceRow.children.forEach((child) => {
              const periodData = child.monthlyData[period];
              if (periodData && periodData.cost) {
                serviceTotals[period] += calculateCostValue(periodData.cost);
              }
            });
          }
        });

        return (
          <TableRow className="ct-tableRow ct-serviceTotalRow">
            <TableCell className="ct-tableCell ct-serviceCell ct-serviceTotalCell">
              <div className="ct-expandableCell ct-level-0">
                <div className="ct-emptyExpandIcon" />
                <span className="ct-serviceName">Total</span>
              </div>
            </TableCell>
            {timeColumns.map((period) => (
              <TableCell
                key={period}
                className="ct-tableCell ct-serviceTotalCell"
                align="center"
              >
                {formatCost(serviceTotals[period])}
              </TableCell>
            ))}
            <TableCell
              className="ct-grandTotalCell ct-serviceTotalCell"
              align="center"
            >
              {formatCost(serviceRow.totalCost)}
            </TableCell>
          </TableRow>
        );
      }
      return null;
    };

    const renderRegionTotal = (
      regionRow: CalculatedRowData,
      regionIndex: number
    ): React.ReactNode => {
      if (
        regionRow.level === 1 &&
        regionRow.children &&
        regionRow.children.length > 0
      ) {
        const regionRowId = `${rowId}-${regionIndex}`;
        const isRegionExpanded = expandedRows.has(regionRowId);

        if (isRegionExpanded) {
          // Calculate region-level totals for each time period
          const regionTotals: { [period: string]: number } = {};
          timeColumns.forEach((period) => {
            regionTotals[period] = 0;
            if (regionRow.children) {
              regionRow.children.forEach((child) => {
                const periodData = child.monthlyData[period];
                if (periodData && periodData.cost) {
                  regionTotals[period] += calculateCostValue(periodData.cost);
                }
              });
            }
          });

          return (
            <TableRow className="ct-tableRow ct-regionTotalRow">
              <TableCell className="ct-tableCell ct-serviceCell ct-regionTotalCell">
                <div className="ct-expandableCell ct-level-1">
                  <div className="ct-emptyExpandIcon" />
                  <span className="ct-regionName">Total</span>
                </div>
              </TableCell>
              {timeColumns.map((period) => (
                <TableCell
                  key={period}
                  className="ct-tableCell ct-regionTotalCell"
                  align="center"
                >
                  {formatCost(regionTotals[period])}
                </TableCell>
              ))}
              <TableCell
                className="ct-grandTotalCell ct-regionTotalCell"
                align="center"
              >
                {formatCost(regionRow.totalCost)}
              </TableCell>
            </TableRow>
          );
        }
      }
      return null;
    };

    return (
      <React.Fragment key={rowId}>
        <TableRow className={`ct-tableRow ${getRowClass(row.level)}`}>
          <TableCell className="ct-tableCell ct-serviceCell">
            <div className={`ct-expandableCell ct-level-${row.level}`}>
              {hasChildren ? (
                <IconButton
                  size="small"
                  onClick={() => toggleExpand(rowId)}
                  className="ct-expandIcon"
                >
                  {isExpanded ? <ExpandMore /> : <ChevronRight />}
                </IconButton>
              ) : (
                <div className="ct-emptyExpandIcon" />
              )}
              <span className={getNameClass(row.level)}>{row.name}</span>
            </div>
          </TableCell>
          {timeColumns.map((period) => {
            const periodData = row.monthlyData[period];
            if (!periodData) {
              return (
                <TableCell key={period} className="ct-tableCell" align="center">
                  $0.00
                </TableCell>
              );
            }
            return (
              <TableCell
                key={period}
                className={`ct-tableCell ${getTrendClass(periodData.trend)}`}
                align="center"
              >
                {periodData.cost || '$0.00'}
              </TableCell>
            );
          })}
          <TableCell className="ct-grandTotalCell" align="center">
            {formatCost(row.totalCost)}
          </TableCell>
        </TableRow>
        {hasChildren && (
          <TableRow>
            <TableCell
              colSpan={timeColumns.length + 2}
              style={{ padding: 0, border: 'none' }}
              align="center"
            >
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {row.children?.map((childRow, childIndex) => (
                      <React.Fragment key={childIndex}>
                        {renderRow(childRow, childIndex, rowId)}
                        {renderRegionTotal(childRow, childIndex)}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
        {renderServiceTotal(row)}
      </React.Fragment>
    );
  };

  const getHeaderText = (period: string): string => {
    if (dataType === 'daily') {
      return `Day ${period}`;
    }
    return period;
  };

  const StatusLegend: React.FC = () => {
    return (
      <div className="statusLegend mt-10">
        <div className="statusItem">
          <span className="statusDot decreasedDot"></span>
          <span className="statusLabel">Decreased</span>
        </div>
        <div className="statusItem">
          <span className="statusDot noChangeDot"></span>
          <span className="statusLabel">No Change</span>
        </div>
        <div className="statusItem">
          <span className="statusDot increasedLowDot"></span>
          <span className="statusLabel">Increased ( 0-5% )</span>
        </div>
        <div className="statusItem">
          <span className="statusDot increasedHighDot"></span>
          <span className="statusLabel">Increased ({' >'}5% )</span>
        </div>
      </div>
    );
  };

  return (
    <div className={`ct-collapsibleTableContainer ${className}`}>
      <div className="ct-tableControls">
        <div
          className={`btn btn-primary expand-toggle-button ${
            isLoading ? 'disabled' : ''
          }`}
          onClick={() => {
            if (!isLoading) handleExpandAll();
          }}
        >
          {areAllRowsExpanded ? (
            <>
              <RemoveIcon className="expand-icon" />
              <span className="expand-text">Collapse All</span>
            </>
          ) : (
            <>
              <AddIcon className="expand-icon" />
              <span className="expand-text">Expand All</span>
            </>
          )}
        </div>
        <StatusLegend />
      </div>
      <div className="ct-scrollableContainer table adminTable adminPanelTable">
        <Table
          className="ct-collapsibleTable"
          size="small"
          aria-label="a dense table"
          stickyHeader
        >
          <TableHead className="ct-tableHeader">
            <TableRow>
              <TableCell
                className="ct-headerCell ct-serviceHeader adminTableheading"
                style={{ zIndex: 10 }}
              >
                Services
              </TableCell>
              {timeColumns.map((period) => (
                <TableCell
                  key={period}
                  className="ct-headerCell ct-monthHeader adminTableheading"
                  align="center"
                >
                  {getHeaderText(period)}
                </TableCell>
              ))}
              <TableCell
                className="ct-headerCell ct-grandTotalHeader adminTableheading"
                style={{ zIndex: 2 }}
                align="center"
              >
                Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="ct-tableBody">
            {isLoading ? (
              <TableRow className="ct-tableRow">
                <TableCell
                  colSpan={timeColumns.length + 2}
                  className="ct-tableCell ct-loadingCell"
                >
                  <Loading style={{ padding: '2rem' }} />
                </TableCell>
              </TableRow>
            ) : !hasData ? (
              <TableRow className="ct-tableRow">
                <TableCell
                  colSpan={timeColumns.length + 2}
                  className="ct-tableCell ct-emptyStateCell"
                >
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              <>
                {processedData.map((row, index) => renderRow(row, index))}

                {/* Time Period Totals Row */}
                <TableRow className="ct-tableRow ct-totalRow">
                  <TableCell className="ct-tableCell ct-serviceCell ct-totalRowCell">
                    <div className="ct-expandableCell ct-level-0">
                      <div className="ct-emptyExpandIcon" />
                      <span className="ct-serviceName">Total</span>
                    </div>
                  </TableCell>
                  {timeColumns.map((period) => (
                    <TableCell
                      key={period}
                      className="ct-tableCell ct-totalRowCell"
                      align="center"
                    >
                      {formatCost(timePeriodTotals[period] || 0)}
                    </TableCell>
                  ))}
                  <TableCell
                    className="ct-grandTotalCell ct-totalRowCell"
                    align="center"
                  >
                    {formatCost(grandTotal)}
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default CollapsibleTable;
